import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Delegated click handling so overlay actions also work inside slideshow slides on mobile.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const actionElement = event.target.closest('[data-pdp-action]');
    if (!(actionElement instanceof HTMLElement)) return;

    const action = actionElement.dataset.pdpAction;
    if (!action) return;

    switch (action) {
      case 'open-video':
        this.openPdpVideoModal(event);
        break;
      case 'open-reviews':
        this.scrollToReviews(event);
        break;
      case 'close-video':
        this.closePdpVideoModal(event);
        break;
      case 'modal-add-to-cart':
        this.addCurrentVariantToCart(event);
        break;
      case 'modal-backdrop':
        this.handlePdpVideoModalBackdropClick(event);
        break;
      default:
        break;
    }
  };

  /**
   * Delegated keyboard handling for modal close.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const modal = this.#getPdpVideoModal();
    if (modal instanceof HTMLDialogElement && modal.open) {
      this.handlePdpVideoModalKeydown(event);
    }
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Open PDP video modal and autoplay media.
   * @param {Event} [event]
   */
  openPdpVideoModal(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const modal = this.#getPdpVideoModal();
    if (!(modal instanceof HTMLDialogElement) || modal.open) return;

    modal.showModal();
    this.#playPdpModalVideo();
  }

  /**
   * Close PDP video modal and pause media.
   * @param {Event} [event]
   */
  closePdpVideoModal(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const modal = this.#getPdpVideoModal();
    if (!(modal instanceof HTMLDialogElement) || !modal.open) return;

    this.#pausePdpModalVideo();
    modal.close();
  }

  /**
   * Close modal when user clicks outside video area.
   * @param {MouseEvent} event
   */
  handlePdpVideoModalBackdropClick(event) {
    if (event.target === this.#getPdpVideoModal()) {
      this.closePdpVideoModal(event);
    }
  }

  /**
   * Keyboard support for closing the video modal.
   * @param {KeyboardEvent} event
   */
  handlePdpVideoModalKeydown(event) {
    if (event.key === 'Escape') {
      this.closePdpVideoModal(event);
    }
  }

  /**
   * Smooth-scroll to Judge.me review section.
   * @param {Event} [event]
   */
  scrollToReviews(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const reviewsSection = document.querySelector('#judgeme_product_reviews');
    if (reviewsSection instanceof HTMLElement) {
      reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Fallback for delayed Judge.me render.
    if (window.location.hash !== '#judgeme_product_reviews') {
      window.location.hash = 'judgeme_product_reviews';
    }
  }

  /**
   * Add current variant to cart and open cart drawer.
   * @param {Event} [event]
   */
  async addCurrentVariantToCart(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const variantId = this.dataset.currentVariantId;
    const addToCartButton = this.querySelector('[data-pdp-modal-atc]');

    if (!variantId || !(addToCartButton instanceof HTMLButtonElement)) return;

    addToCartButton.setAttribute('aria-busy', 'true');

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const cartItemsComponents = document.querySelectorAll('cart-items-component[data-section-id]');
      const sectionIds = Array.from(cartItemsComponents)
        .map((component) => component.getAttribute('data-section-id'))
        .filter(Boolean);

      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
      }

      const addResponse = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!addResponse.ok) {
        throw new Error('Unable to add the selected variant to cart.');
      }

      const cartResponse = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CartAddEvent(cart, `media-gallery-${this.dataset.productId || ''}`, {
          source: 'media-gallery-pdp-video',
          variantId,
          itemCount: 1,
        })
      );

      const cartDrawer = document.querySelector('cart-drawer-component');
      cartDrawer?.open();
      this.closePdpVideoModal();
    } catch (error) {
      console.error(error);
    } finally {
      addToCartButton.removeAttribute('aria-busy');
    }
  }

  #playPdpModalVideo() {
    const video = this.querySelector('[data-pdp-video-player]');
    if (video instanceof HTMLVideoElement) {
      video.currentTime = 0;
      video.play().catch(() => {});
      return;
    }

    const iframe = this.querySelector('[data-pdp-video-iframe]');
    if (!(iframe instanceof HTMLIFrameElement)) return;

    const iframeSource = iframe.dataset.src;
    if (iframeSource && iframe.getAttribute('src') !== iframeSource) {
      iframe.setAttribute('src', iframeSource);
    }
  }

  #pausePdpModalVideo() {
    const video = this.querySelector('[data-pdp-video-player]');
    if (video instanceof HTMLVideoElement) {
      video.pause();
      video.currentTime = 0;
      return;
    }

    const iframe = this.querySelector('[data-pdp-video-iframe]');
    if (iframe instanceof HTMLIFrameElement) {
      iframe.setAttribute('src', '');
    }
  }

  #getPdpVideoModal() {
    return this.querySelector('[data-pdp-video-modal]');
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
